import { ColumnDef } from '@tanstack/react-table'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { OcrResultWithPage } from 'features/knowledgeDocumentViewer/hooks/useOcrResults'
import { DocumentViewerAssetType } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { ArrowRight, Flag, Loader2, Search } from 'lucide-react'
import React from 'react'
import styled, { keyframes } from 'styled-components'

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const LoadingSpinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`

const TextCell = styled.span`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
`

export const formatAssetType = (type: string | undefined): string => {
  if (!type) return '–'

  return type
    .trim()
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export type OcrTableData = OcrResultWithPage & {
  refsCount: number
}

export type OcrTableColumnsProps = {
  isLoadingRefs?: boolean
  onZoomToBox: (result: OcrTableData) => void
  onFlagClick: (result: OcrTableData) => void
  onViewLinkedClick: (result: OcrTableData, event: React.MouseEvent) => void
  onResultHover?: (result: OcrTableData | null) => void
  canNavigateOffPageConnectors?: boolean
}

export const createOcrTableColumns = ({
  isLoadingRefs,
  onZoomToBox,
  onFlagClick,
  onViewLinkedClick,
  onResultHover,
  canNavigateOffPageConnectors = false,
}: OcrTableColumnsProps): ColumnDef<OcrTableData>[] => [
  {
    accessorKey: 'text',
    header: 'Text',
    cell: ({ row }) => (
      <TextCell title={row.original.text}>{row.original.text || '–'}</TextCell>
    ),
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => (
      <Text as='span' transform='capitalize'>
        {formatAssetType(row.original.type)}
      </Text>
    ),
  },
  {
    id: 'refs',
    header: 'Refs',
    accessorKey: 'refsCount',
    sortingFn: 'basic',
    cell: ({ row }) => {
      if (isLoadingRefs) {
        return (
          <Text as='span' align='center'>
            <LoadingSpinner size={12} />
          </Text>
        )
      }
      return (
        <Text as='span' align='center'>
          {row.original.refsCount}
        </Text>
      )
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const result = row.original
      const hasLinkedDocs = result.refsCount > 0
      const isOffPageConnector =
        result.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR
      const canNavigate =
        (canNavigateOffPageConnectors && isOffPageConnector) ||
        (!isLoadingRefs && hasLinkedDocs)

      return (
        <ActionsContainer
          onMouseEnter={() => onResultHover?.(result)}
          onMouseLeave={() => onResultHover?.(null)}>
          <Button
            variant='ghost'
            size='icon-sm'
            title='Report Issue'
            onClick={() => onFlagClick(result)}>
            <Flag width={16} height={16} />
          </Button>
          <Button
            variant='ghost'
            size='icon-sm'
            title='Zoom to box'
            onClick={() => onZoomToBox(result)}>
            <Search width={16} height={16} />
          </Button>
          <Button
            variant='ghost'
            size='icon-sm'
            title={
              isOffPageConnector
                ? 'Navigate to linked document'
                : 'View linked documents'
            }
            disabled={!canNavigate}
            onClick={(e) => onViewLinkedClick(result, e)}>
            <ArrowRight width={16} height={16} />
          </Button>
        </ActionsContainer>
      )
    },
    enableHiding: false,
  },
]
